import { type InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import Select from './Select';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

interface BaseProps {
  label?: string;
  error?: string;
  className?: string;
  required?: boolean;
}

interface TextInputProps extends BaseProps, InputHTMLAttributes<HTMLInputElement> {
  type?: string;
}

interface TextAreaProps extends BaseProps, TextareaHTMLAttributes<HTMLTextAreaElement> {
  type: 'textarea';
}

interface SelectProps extends BaseProps {
  type: 'select';
  options: Array<{ value: string; label: string }>;
}

interface PhoneProps extends BaseProps {
  type: 'tel';
  value?: string;
  onChange?: (value?: string) => void;
}

type InputProps = TextInputProps | TextAreaProps | SelectProps | PhoneProps;

function Input({ label, error, className = '', ...props }: InputProps) {
  const baseInputClassName = `px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm 
    focus:outline-none focus:ring-2 focus:ring-[#7AD817] focus:border-[#7AD817]
    disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed 
    ${error ? 'border-red-500' : ''}`;

  const containerClassName = 'flex flex-col gap-1 w-full';

  const inputClasses = `${baseInputClassName} ${className}`;

  function renderInputError(error: string | undefined) {
    return error ? <p className='text-sm text-red-600'>{error}</p> : null;
  }

  if (props.type === 'textarea') {
    return (
      <div className={containerClassName}>
        {label && (
          <label className='text-sm font-medium text-gray-700'>
            {label}
            {props.required && <span className='ml-1'>(required)*</span>}
          </label>
        )}
        <textarea className={inputClasses} {...(props as TextAreaProps)} />
        {renderInputError(error)}
      </div>
    );
  }

  if (props.type === 'select') {
    const { options, ...otherProps } = props as SelectProps;
    return (
      <Select
        label={label}
        error={error}
        className={className}
        baseClassName={baseInputClassName}
        options={options}
        {...otherProps}
      />
    );
  }

  if (props.type === 'tel') {
    const { value, onChange, ...otherProps } = props as PhoneProps;

    return (
      <div className={containerClassName}>
        {label && (
          <label className='text-sm font-medium text-gray-700'>
            {label}
            {props.required && <span className='ml-1'>(required)*</span>}
          </label>
        )}
        <div>
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry='GB'
            className={inputClasses}
            value={value}
            onChange={onChange || (() => {})}
            {...otherProps}
          />
        </div>
        {renderInputError(error)}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {label && (
        <label className='text-sm font-medium text-gray-700'>
          {label}
          {props.required && <span className='ml-1'>(required)*</span>}
        </label>
      )}
      <input className={inputClasses} {...(props as TextInputProps)} />
      {renderInputError(error)}
    </div>
  );
}

export default Input;
